/**
 * Light or dark, remembered.
 *
 * Three states rather than two. "System" is the default and is not a third palette — it is
 * the absence of a choice, which lets the stylesheet answer through `prefers-color-scheme`.
 * Storing "light" the moment someone opens the panel would silently override an operating
 * system set to dark, so nothing is written until a choice is actually made.
 *
 * Applied by stamping `data-theme` on the root element, which is what the stylesheet keys
 * on; the media query is written to defer to that attribute whenever it is present.
 */

export type Theme = 'light' | 'dark' | 'system';

const KEY = 'xniq.theme';

export function storedTheme(): Theme {
  const value = localStorage.getItem(KEY);
  return value === 'light' || value === 'dark' ? value : 'system';
}

export function applyTheme(theme: Theme): void {
  const root = document.documentElement;

  if (theme === 'system') {
    root.removeAttribute('data-theme');
    localStorage.removeItem(KEY);
    return;
  }

  root.setAttribute('data-theme', theme);
  localStorage.setItem(KEY, theme);
}

/** What the panel is actually showing, which "system" alone does not tell you. */
export function resolvedTheme(theme: Theme): 'light' | 'dark' {
  if (theme !== 'system') return theme;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
